import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AccordionSection } from '../accordion-table-view/accordion-table-view.component';
import { DynamicFormComponent } from '../dynamic-form/dynamic-form.component';
import { FeatureToggleCardComponent } from '../feature-toggle-card/feature-toggle-card.component';
import { ProjectService } from '../../../core/services/project/project.service';
import { RestService } from '../../../core/services/rest.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { MessageEnum } from '../../../core/utils/message.enum';

export interface CicdStep2Result {
  sections: AccordionSection[];
  linkToRelease: boolean;
  releaseId: string | null;
  testFailureAnalysis: boolean;
  uiSelfHealing: boolean;
}

@Component({
  selector: 'app-cicd-execution-step2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    FeatureToggleCardComponent,
    DynamicFormComponent,
  ],
  templateUrl: './cicd-execution-step2.component.html',
  styleUrl: './cicd-execution-step2.component.scss',
})
export class CicdExecutionStep2Component implements OnInit {
  readonly activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);
  private readonly projectService = inject(ProjectService);
  private readonly toaster = inject(ToasterService);

  /** Sections carried over from step 1. */
  @Input() sections: AccordionSection[] = [];

  /**
   * Injected by the caller (cicd-executions.component) so this component
   * never needs to subscribe to ProjectService itself when IDs are already
   * resolved. Falls back to ProjectService if not provided.
   */
  @Input() projectId: string = '';
  @Input() automationProjectId: string = '';

  // ── Edit mode ─────────────────────────────────────────────────────────────
  @Input() editMode: boolean = false;
  @Input() pipelineId: string = '';
  @Input() initialLinkToRelease: boolean = true;
  @Input() initialReleaseId: string | null = null;
  @Input() initialTestFailureAnalysis: boolean = false;
  @Input() initialUiSelfHealing: boolean = false;
  @Input() initialName: string = '';

  // ── Feature flags ──────────────────────────────────────────────────────────
  executionName: string = '';
  linkToRelease: boolean = true;
  testFailureAnalysis: boolean = false;
  uiSelfHealing: boolean = false;

  // ── Release – State A (existing dropdown) ──────────────────────────────────
  releases: any[] = [];
  selectedReleaseId: string = '';
  isLoadingReleases: boolean = false;

  // ── Release – State B (inline create form) ─────────────────────────────────
  showReleaseForm: boolean = false;
  isCreatingRelease: boolean = false;

  releaseForm = new FormGroup({
    releaseName: new FormControl('', Validators.required),
    startDate:   new FormControl<Date | null>(null, Validators.required),
    endDate:     new FormControl<Date | null>(null, Validators.required),
    description: new FormControl(''),
  });

  readonly releaseFields: any[] = [
    {
      key: 'releaseName',
      name: 'Release name',
      type: 'text-input',
      placeholder: 'Enter release name',
      wrapperClass: 'col-md-12',
      validations: { required: true },
    },
    {
      key: 'startDate',
      name: 'Start Date',
      type: 'date-picker',
      wrapperClass: 'col-md-6',
      validations: { required: true },
    },
    {
      key: 'endDate',
      name: 'End Date',
      type: 'date-picker',
      wrapperClass: 'col-md-6',
      validations: { required: true },
    },
    {
      key: 'description',
      name: 'Release description',
      type: 'textarea',
      placeholder: 'Enter release description',
      wrapperClass: 'col-md-12',
    },
  ];

  // ── Submission state ───────────────────────────────────────────────────────
  isSaving: boolean = false;

  ngOnInit(): void {
    // Apply initial values when editing an existing pipeline
    if (this.editMode) {
      this.linkToRelease = this.initialLinkToRelease;
      this.testFailureAnalysis = this.initialTestFailureAnalysis;
      this.uiSelfHealing = this.initialUiSelfHealing;
      if (this.initialReleaseId) {
        this.selectedReleaseId = this.initialReleaseId;
      }
      if (this.initialName) {
        this.executionName = this.initialName;
      }
    }

    // Use IDs passed directly from the caller if available; fall back to service
    if (this.projectId && this.automationProjectId) {
      if (this.linkToRelease) this.loadReleases();
    } else {
      this.projectService.getProjectId().subscribe(id => {
        if (id) {
          this.projectId = id;
          if (this.linkToRelease) this.loadReleases();
        }
      });
    }
  }

  // ── Toggle handlers ────────────────────────────────────────────────────────
  onLinkToReleaseEnabled(): void {
    this.showReleaseForm = false;
    this.selectedReleaseId = '';
    this.loadReleases();
  }

  onLinkToReleaseDisabled(): void {
    this.showReleaseForm = false;
    this.selectedReleaseId = '';
    this.releases = [];
    this.releaseForm.reset();
  }

  // ── Release helpers ────────────────────────────────────────────────────────
private loadReleases(): void {
  this.isLoadingReleases = true;

  this.restService.getReleases(this.projectId).subscribe({
    next: (res: any) => {
      this.releases = res?.data ?? [];

      if (this.editMode && this.initialReleaseId) {
        this.selectedReleaseId = this.initialReleaseId;
      }

      this.isLoadingReleases = false;
    },
    error: () => {
      this.releases = [];
      this.isLoadingReleases = false;
      this.toaster.pop({ type: 'error', text: MessageEnum.RELEASE_LOAD_FAILURE });
    },
  });
}

  addNewRelease(): void {
    this.releaseForm.reset();
    this.showReleaseForm = true;
  }

  confirmRelease(): void {
    if (this.releaseForm.invalid || this.isCreatingRelease) return;

    const { releaseName, startDate, endDate, description } = this.releaseForm.value;
    const payload = {
      name: releaseName,
      startDate:   this.toISODateTimeStr(startDate),
      releaseDate: this.toISODateTimeStr(endDate),
      description: description ?? '',
    };

    this.isCreatingRelease = true;
    this.restService.createRelease(this.projectId, payload).subscribe({
      next: (res: any) => {
        const created = res?.data ?? { id: res?.id, name: payload.name };
        this.releases = [...this.releases, created];
        this.selectedReleaseId = created.id;
        this.showReleaseForm = false;
        this.releaseForm.reset();
        this.isCreatingRelease = false;
      },
      error: () => {
        this.isCreatingRelease = false;
        this.toaster.pop({ type: 'error', text: 'Failed to create release.' });
      },
    });
  }

  cancelRelease(): void {
    this.showReleaseForm = false;
    this.releaseForm.reset();
  }

  // ── Footer actions ─────────────────────────────────────────────────────────
  get isSaveDisabled(): boolean {
    if (!this.executionName.trim()) return true;
    if (this.linkToRelease) {
      if (this.showReleaseForm) return this.releaseForm.invalid;
      return !this.selectedReleaseId;
    }
    return false;
  }

  onBack(): void {
    this.activeModal.close({ back: true, sections: this.sections, editMode: this.editMode, pipelineId: this.pipelineId });
  }

  onCancel(): void {
    this.activeModal.dismiss('Cross click');
  }

  onSave(): void {
    if (this.isSaveDisabled || this.isSaving) return;
    this.isSaving = true;

    const payload = this.buildPayload();
    const request$ = (this.editMode && this.pipelineId)
      ? this.restService.updatePipelineConfig(this.projectId, this.automationProjectId, this.pipelineId, payload)
      : this.restService.savePipelineConfig(this.projectId, this.automationProjectId, payload);

    request$.subscribe({
      next: () => {
        const result: CicdStep2Result = {
          sections: this.sections,
          linkToRelease: this.linkToRelease,
          releaseId: this.linkToRelease ? this.selectedReleaseId : null,
          testFailureAnalysis: this.testFailureAnalysis,
          uiSelfHealing: this.uiSelfHealing,
        };
        this.isSaving = false;
        this.activeModal.close(result);
      },
      error: () => {
        this.isSaving = false;
        this.toaster.pop({ type: 'error', text: MessageEnum.PIPELINE_SAVE_FAILURE });
      },
    });
  }

  // ── Payload builder ────────────────────────────────────────────────────────
  private buildPayload(): any {
    const ADHOC_NAME = 'AdHocTestplan';
    const automationExecutionPlanRequestDTOS = this.sections.map((section, _) => {
      const isAdhoc = section.id === 'adhoc-test-plan';
      const executionScripts = (section.tableRows ?? []).map((row: any, idx: number) => {
        const script: any = {
          sequence: idx,
          id: row.testCaseId,
          name: row.testCaseName,
          browser: row.browser ?? 'Chrome',
          version: row.version ?? 'v1.0.0',
          increment: row.increment ?? 0,
        };

        const engineValue = row.executionEngine;
        if (engineValue === 'onDemand' || row.agentType === 'CLUSTER' || !engineValue) {
          script.agentType = 'CLUSTER';
          script.executionType = 'WEB';
        } else if (row.deviceId || (typeof engineValue === 'string' && engineValue.toLowerCase().includes('device'))) {
          script.deviceId = row.deviceId ?? engineValue;
          script.executionType = 'MOBILE';
        } else {
          script.agentId = engineValue;
          script.agentType = row.agentType ?? 'LOCAL';
          script.executionType = row.executionType ?? 'WEB';
        }

        return script;
      });

      const rawId = section.id as string;
      let testPlanId: string | undefined;
      if (isAdhoc) {
        testPlanId = undefined;
      } else {
        // Prefer _nodeId (pure UUID set by modal); fall back to stripping 'plan-' prefix
        const nodeId = (section as any)._nodeId as string | undefined;
        if (nodeId) {
          testPlanId = nodeId;
        } else if (rawId.startsWith('plan-')) {
          // Legacy format: 'plan-{uuid}' — take the first UUID-shaped segment
          const afterPrefix = rawId.slice('plan-'.length);
          const uuidMatch = /^[0-9a-f-]{36}/i.exec(afterPrefix);
          testPlanId = uuidMatch ? uuidMatch[0] : afterPrefix;
        } else {
          testPlanId = rawId;
        }
      }

      return {
        testPlanId,
        testPlanName: isAdhoc ? ADHOC_NAME : (section.name ?? section.testSuiteName ?? section.testPlanName),
        executionScripts,
      };
    });

    return {
      name: this.executionName.trim(),
      testFailureAnalysis: this.testFailureAnalysis,
      selfHealing: this.uiSelfHealing,
      releaseId: this.linkToRelease ? (this.selectedReleaseId || null) : null,
      automationExecutionPlanRequestDTOS,
    };
  }

  // ── Utils ──────────────────────────────────────────────────────────────────
  private toISODateTimeStr(d: any): string {
    if (!d) return '';
    const iso = new Date(d).toISOString();
    const withoutZ = iso.slice(0, -1);
    const dotIdx = withoutZ.lastIndexOf('.');
    const ms = withoutZ.slice(dotIdx + 1);
    return withoutZ.slice(0, dotIdx + 1) + ms.padEnd(6, '0');
  }
}
